/* Copyright (c) 2023-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useState } from "react";
import { theme } from "@nordcloud/gnui";
import {
  ResourceStatus,
  ResourcesSortFields,
  SortOrder,
  useGetResourcesLazyQuery,
  OsType,
  Resource,
} from "~/generated/graphql";
import { StatusChartItem } from "../../components/types";

const REQUEST_LIMIT = 50;

type ResourceListItem = {
  id: string;
  osType: OsType | undefined;
  hasAccess: boolean;
};

export function useGetDashboardResources() {
  const [resourceList, setResourceList] = useState<ResourceListItem[]>([]);

  const [getData, { data: resourcesData, loading, fetchMore, error }] =
    useGetResourcesLazyQuery();

  const getResources = useCallback(
    () =>
      getData({
        variables: {
          page: 1,
          limit: REQUEST_LIMIT,
          sort: {
            field: ResourcesSortFields.Name,
            order: SortOrder.Asc,
          },
        },
      }),
    [getData]
  );

  useEffect(() => {
    setResourceList(() =>
      mapInput((resourcesData?.resources?.result ?? []) as Resource[])
    );
  }, [resourcesData?.resources?.result]);

  const count = resourcesData?.resources?.pageInfo.count ?? 0;
  const pages = resourcesData?.resources?.pageInfo?.pages ?? 0;

  useEffect(() => {
    if (!loading && count > (resourcesData?.resources?.result?.length ?? 0)) {
      [...Array(pages).keys()].slice(1).forEach((element) => {
        fetchMore?.({
          variables: {
            page: element + 1,
            limit: REQUEST_LIMIT,
            sort: {
              field: ResourcesSortFields.Name,
              order: SortOrder.Asc,
            },
          },
        }).then(({ data: fetchedData }) => {
          const newResourcesList = mapInput(
            (fetchedData.resources?.result ?? []) as Resource[]
          );

          return setResourceList((prev) => [...prev, ...newResourcesList]);
        });
      });
    }
  }, [
    fetchMore,
    loading,
    count,
    pages,
    resourcesData?.resources?.result?.length,
  ]);

  return {
    getResources,
    resourcesStatus: getResourcesStatus(resourceList),
    resourcesOsList: getOsList(resourceList),
    loading,
    error,
    count,
  } as const;
}

const mapInput = (input: Resource[]): ResourceListItem[] => {
  return input.map((item, index) => ({
    id: item?.id ?? index.toString(),
    osType: item?.os?.type ?? undefined,
    hasAccess: item?.state?.status === ResourceStatus.Online,
  }));
};

const getResourcesStatus = (list: ResourceListItem[]): StatusChartItem[] => {
  return [
    {
      label: "Lost Connection",
      count: list.filter(({ hasAccess }) => !hasAccess).length,
      color: theme.color.background.error,
    },
    {
      label: "Online",
      count: list.filter(({ hasAccess }) => hasAccess).length,
      color: theme.color.background.success,
    },
  ];
};

const OS_COLOR = {
  [OsType.Linux]: "#2196f3",
  [OsType.Windows]: "#8e44ad",
};

const getOsList = (list: ResourceListItem[]): StatusChartItem[] => {
  return Object.values(OsType).map((osType) => ({
    label: osType,
    count: list.filter((item) => item.osType === osType).length,
    color: OS_COLOR[osType],
  }));
};
